import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";
const BASE_URL = process.env.REACT_APP_BASE_URL;

// all establishment documents
export const fetchEstablishmentDocuments = createAsyncThunk(
  "establishment_documents/fetchAll",
  async ({ page = 1, status = "", document_number = "", establishment_id = "" ,
    is_auto_renewal = "",
    has_expire_date = "" }, { rejectWithValue }) => {
    try {
      const normalize = (val) => {
        if (val === "true" || val === true) return 1;
        if (val === "false" || val === false) return 0;
        return "";
      };
      const response = await axios.get(`${BASE_URL}/establishment-documents?page=${page}&status=${status}&establishment_id=${establishment_id}&document_number=${document_number}&is_auto_renewal=${normalize(is_auto_renewal)}&has_expire_date=${normalize(has_expire_date)}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data; // Return the whole response including pagination
    } catch (error) {
      const message = error.response?.data?.message || "Failed load data";
      return rejectWithValue(message);
    }
  }
);

// add establishment documents
export const addEstablishmentDocuments = createAsyncThunk(
  "establishment_documents/add",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}/establishment-documents`, userData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "multipart/form-data",
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "An unknown error occurred";
      return rejectWithValue(message);
    }
  }
);
// add establishment documents comments
export const addEstablishmentDocumentsComments = createAsyncThunk(
  "establishment_documents/comments",
  async ({ comment, id }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}/establishment-documents/${id}/comments`, comment, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "An unknown error occurred";
      return rejectWithValue(message);
    }
  }
);
// get establishment documents record
export const establishmentDocumentsRecord = createAsyncThunk(
  "establishment_documents/establishmentDocumentsRecord",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/establishment-documents/${id}`);
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "An unknown error occurred";
      return rejectWithValue({ message });
    }
  }
);
// Fetch all establishment documents once (for filters)
export const fetchAllEstablishmentDocuments = createAsyncThunk(
  "establishment_documents/fetchAllFull",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/establishment-documents`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load all data";
      return rejectWithValue(message);
    }
  }
);
const EstablishmentDocumentsSlice = createSlice({
  name: "establishment_documents",
  initialState: {
    establishment_documents: [],
    all_establishment_documents: [],
    record: null,
    isLoading: false,
    error: null,
    success: null,
    successComment: null,
    errorComment: null,
    successDelete: null,
    errorDelete: null,
    pagination: null,
  },
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEstablishmentDocuments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEstablishmentDocuments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.establishment_documents = action.payload.data.data;
        state.pagination = action.payload.data.pagination;
      })
      .addCase(fetchEstablishmentDocuments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to load data";
      })
      .addCase(fetchAllEstablishmentDocuments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllEstablishmentDocuments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.all_establishment_documents = action.payload.data.data;
      })
      .addCase(fetchAllEstablishmentDocuments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to load all establishment documents";
      })
      .addCase(addEstablishmentDocuments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addEstablishmentDocuments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(addEstablishmentDocuments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to add data";
      })
      .addCase(addEstablishmentDocumentsComments.pending, (state) => {
        state.isLoading = true;
        state.errorComment = null;
      })
      .addCase(addEstablishmentDocumentsComments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.successComment = action.payload.message;
      })
      .addCase(addEstablishmentDocumentsComments.rejected, (state, action) => {
        state.isLoading = false;
        state.errorComment = action.payload || "Failed to add data";
      })
      .addCase(establishmentDocumentsRecord.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(establishmentDocumentsRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        state.record = action.payload.data;
      })
      .addCase(establishmentDocumentsRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to add data";
      });
  },
});

export const { clearState } = EstablishmentDocumentsSlice.actions;
export default EstablishmentDocumentsSlice.reducer;